import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Button, Modal, Col, Alert, Icon, Row } from '@storable/canaveral-components';
import MediaGallerySelector from '../../containers/MediaGallerySelector';
import { INPUT_PERMITTED_IMAGES_TYPES } from '../../../modules/assets';
import Input from '../../../modules/react/components/stateful/Input';
import Field from '../../../modules/react/components/stateful/Field';
import styles from './styles.scss';

const propTypes = {
  loading: PropTypes.bool.isRequired,
  onUpload: PropTypes.func.isRequired,
  featuredImage: PropTypes.shape({
    id: PropTypes.shape({
      input: PropTypes.shape({
        value: PropTypes.string,
        onChange: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
    title: PropTypes.shape({
      input: PropTypes.shape({
        value: PropTypes.string,
        onChange: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
    altText: PropTypes.shape({
      input: PropTypes.shape({
        value: PropTypes.string,
        onChange: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  image: PropTypes.shape({
    trash: PropTypes.bool,
    filename: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
};

const defaultProps = {
  image: undefined,
};

const BlogMediumField = ({ image, loading, onUpload, featuredImage }) => {
  const [showMediaGallery, setShowMediaGallery] = useState();
  const [selectedMedium, setSelectedMedium] = useState();
  const uploadRef = useRef();

  const handleSelectImage = () => {
    featuredImage.id.input.onChange(selectedMedium.id);
    setShowMediaGallery(false);
  };

  const handleReset = () => {
    featuredImage.id.input.onChange(null);
    featuredImage.altText.input.onChange('');
    featuredImage.title.input.onChange('');
  };

  return (
    <>
      <Modal show={showMediaGallery} onDismiss={() => setShowMediaGallery(false)} size="lg">
        <Modal.Header>
          <Modal.Title id="media-gallery">Media Gallery</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MediaGallerySelector
            usedMedia={featuredImage.id.input.value ? [featuredImage.id.input.value] : []}
            hideGallery={() => setShowMediaGallery(false)}
            setSelectedMedium={setSelectedMedium}
            showSearch
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            context="neutral"
            onClick={() => setShowMediaGallery(false)}
            size="sm"
          >
            Cancel
          </Button>
          <Button
            context="positive"
            disabled={!selectedMedium}
            onClick={handleSelectImage}
            size="sm"
          >
            Choose Image
          </Button>
        </Modal.Footer>
      </Modal>
      <div>
        <div className={styles.label} htmlFor="image-input">
          Featured Image
        </div>
        <input
          accept={INPUT_PERMITTED_IMAGES_TYPES}
          className={styles.uploadInput}
          ref={uploadRef}
          type="file"
          onChange={onUpload}
          name="image-input"
        />
        <Button
          context="neutral"
          disabled={loading}
          icon="upload"
          onClick={() => uploadRef.current.click()}
        >
          Upload Image
        </Button>
        <Button
          context="neutral"
          disabled={loading}
          icon="images"
          onClick={() => setShowMediaGallery(true)}
          className={styles.galleryButton}
        >
          Select from Gallery
        </Button>
        {image?.trash && (
          <Alert context="warning" className={styles.warning}>
            <Icon id="warning-triangle" label="deleted image" className={styles.warningIcon} />
            <div className={styles.warningText}>
              This image is in the trash and will not display on your website unless it&apos;s
              restored in the Media Gallery. The default image and alt text are currently
              displaying on your site.
            </div>
          </Alert>
        )}
        {image && (
          <>
            <div>
              <>
                <Row>
                  <Col xs={10} className={styles.filename}>
                    {image.filename}
                  </Col>
                  <Col xs={2}>
                    <Button
                      icon="trash"
                      label="Remove Image"
                      context="bare"
                      className={styles.resetButton}
                      onClick={handleReset}
                      data-testId="reset-image"
                    />
                  </Col>
                </Row>
                <img
                  alt={featuredImage.altText.input.value}
                  className={styles.image}
                  src={image.url}
                />
              </>
            </div>
            <div>
              <Field
                label="Alt Text"
                name="featuredImage.altText"
                component={Input}
              />
              <Field
                label="Title"
                name="featuredImage.title"
                component={Input}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

BlogMediumField.propTypes = propTypes;
BlogMediumField.defaultProps = defaultProps;

BlogMediumField.fragments = {
  medium: gql`
    fragment BlogMediumFieldMedium on Medium {
      url
      filename
      defaultMetadata {
        title
        altText
      }
      trash
    }
  `,
};

export default BlogMediumField;
